import { Trans, useLingui } from '@lingui/react/macro'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'designSystem/components/AlertDialog'
import { Button } from 'designSystem/components/Button'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { TagConfigurationList } from 'features/timeSeries/components/panes/TagConfigurationList'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { WorkbenchPane } from 'features/workbench/WorkbenchPane'
import { RotateCcw, Settings2, Trash2 } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'

const PaneScroller = styled.div`
  overflow-y: auto;
  height: 100%;
`

interface TagConfigurationPaneProps {
  grow?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const TagConfigurationPane: React.FC<TagConfigurationPaneProps> = ({
  grow = false,
  open,
  onOpenChange,
}) => {
  const { t } = useLingui()

  const {
    selectedSensors,
    setSelectedSensors,
    autoScaleMode,
    handleAutoScaleAll,
  } = useScatterplotContext()
  const [confirmClear, setConfirmClear] = useState(false)
  const [showControls, setShowControls] = useState(true)

  return (
    <WorkbenchPane
      title={t`Tag Configuration`}
      grow={grow}
      defaultOpen={false}
      open={open}
      onOpenChange={onOpenChange}
      actions={
        <>
          <ChartTooltip label={t`Revert to auto Y-scaling`} side='bottom'>
            <Button
              variant='ghostMuted'
              size='icon-xs'
              aria-label={t`Revert to auto Y-scaling`}
              disabled={autoScaleMode}
              onClick={handleAutoScaleAll}>
              <RotateCcw size={12} />
            </Button>
          </ChartTooltip>
          <ChartTooltip
            label={
              showControls ? t`Hide chart controls` : t`Show chart controls`
            }
            side='bottom'>
            <Button
              variant='ghostMuted'
              size='icon-xs'
              pressed='accent'
              aria-pressed={showControls}
              aria-label={
                showControls ? t`Hide chart controls` : t`Show chart controls`
              }
              onClick={() => setShowControls((current) => !current)}>
              <Settings2 size={12} />
            </Button>
          </ChartTooltip>
          {selectedSensors.length > 0 && (
            <ChartTooltip label={t`Remove all pens`} side='bottom'>
              <Button
                variant='ghostMuted'
                size='icon-xs'
                aria-label={t`Remove all pens`}
                onClick={() => setConfirmClear(true)}>
                <Trash2 size={12} />
              </Button>
            </ChartTooltip>
          )}
        </>
      }>
      <PaneScroller>
        <TagConfigurationList showControls={showControls} />
      </PaneScroller>
      <AlertDialog
        open={confirmClear}
        onOpenChange={(open) => {
          if (!open) setConfirmClear(false)
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Remove all pens?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Every tag comes off the chart. The view itself is not deleted.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              onClick={() => {
                setSelectedSensors([])
                setConfirmClear(false)
              }}>
              <Trans>Remove all</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkbenchPane>
  )
}
